import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import EntityForm from "../EntityForm/EntityForm";
import "./customStyles.css";
import NestedQuestionnairesList from "./NestedQuestionnaires/NestedQuestionnairesList";
import NestedQuestionnairesForm from "./NestedQuestionnaires/NestedQuestionnairesForm";
import CONSTANTS from "../constants";
import asBookingEngineEntity from "../Common/BookingEngineEntityHOC";
import { withPropertyData } from "../../../../components/property-admin-data-provider";
import { usePropertyLocales } from "../../../../components/property-locale-provider";
import { useInventory } from "../../../extras/EditExtra/hooks/inventory.hook";
import SmartSelectorV2 from "../Common/SmartSelectorV2";
import SmartSelectorList from "../Common/SmartSelectorList";
import { useIntl, defineMessages } from "react-intl";
import { filterMessages } from "../../../extras/messages";
import { QuestionnaireFormContext } from "./QuestionnaireFormContext";
import EntityErrorMessage from "../Common/EntityErrorMessage";
import { customEntityToFormValuesMapper } from "./questionnaireMappers";
import { prepareEntityForSubmit } from "./questionnaireSerializer";
import { DependerType, INVENTORY_SELECTOR_TYPES, ENGINE_SUPPORTED_LOCALES } from "./dependerTypes";

const QUESTIONNAIRE_VALIDATION_MESSAGES = defineMessages({
  ROOMSTAY_CHECKOUT_MAX_ONE_FIELD: {
    id: "app.questionnaire.validation.roomstayCheckoutMaxOneField",
    defaultMessage: "A ROOMSTAY_CHECKOUT form can only have one field",
  },
  TICKERS_REQUIRED: {
    id: "app.questionnaire.validation.tickersRequired",
    defaultMessage: "At least one inventory line is required to create the form",
  },
});

const nestedFormWidgets = {
  fields: asBookingEngineEntity(NestedQuestionnairesForm, {
    entityName: CONSTANTS.ENTITY.NESTEDQUESTIONNAIRE.name,
    type: "FORM",
  }),
};

const nestedListWidgets = {
  fields: asBookingEngineEntity(NestedQuestionnairesList, {
    entityName: CONSTANTS.ENTITY.NESTEDQUESTIONNAIRE.name,
    type: "LIST",
  }),
};

function InventorySection({ title, children }) {
  return (
    // `witadmin-bs3-parent` paints a light-blue background; override to transparent
    // so the enclosing form panel's white shows through.
    <div
      className="witadmin-bs3 witadmin-bs3-parent"
      style={{ marginTop: "8px", marginBottom: "24px", background: "transparent" }}
    >
      <span style={{ fontWeight: 600, display: "block", marginBottom: "10px" }}>{title}</span>
      {children}
    </div>
  );
}

function QuestionnairesForm({
  locales: localesProp,
  entity,
  entityError,
  entityResult,
  loading,
  getListPath,
  ...otherProps
}) {
  const { name, validation, fields, renderers, translation, entities } = otherProps.config;

  const intl = useIntl();

  // usePropertyLocales returns [] while loading, on failure, and when none are set — fall back in all three.
  const propertyLocales = usePropertyLocales();
  const locales = localesProp ?? (propertyLocales.length ? propertyLocales : ENGINE_SUPPORTED_LOCALES);

  const {
    allowedInventoryLines,
    allowedChainInventoryLinesOptions,
    allowedChainInventoryLinesTickers,
    loadInventoryLinesAllowed,
    loadChainInventoryLinesAllowed,
  } = useInventory();

  const filterNames = useMemo(
    () => [
      intl.formatMessage(filterMessages.accommodation),
      intl.formatMessage(filterMessages.conditions),
      intl.formatMessage(filterMessages.ocupation),
      intl.formatMessage(filterMessages.mealplan),
    ],
    [intl]
  );

  // EntityForm owns the form values and doesn't re-render this parent on internal changes,
  // so the dependersBoxes visible() callback below schedules these updates (refs guard it).
  const currentDependerTypeRef = useRef(entity?.dependers?.[0]?.type || DependerType.EXTRA);
  const currentFieldsCountRef = useRef(entity?.fields?.length || 0);

  const [currentDependerType, setCurrentDependerType] = useState(
    entity?.dependers?.[0]?.type || DependerType.EXTRA
  );
  const [currentFieldsCount, setCurrentFieldsCount] = useState(entity?.fields?.length || 0);
  const [selectedTickers, setSelectedTickers] = useState(() =>
    (entity?.dependers?.[0]?.tickers || []).filter(Boolean)
  );

  useEffect(() => {
    const type = entity?.dependers?.[0]?.type || DependerType.EXTRA;
    const tickers = (entity?.dependers?.[0]?.tickers || []).filter(Boolean);
    currentDependerTypeRef.current = type;
    currentFieldsCountRef.current = entity?.fields?.length || 0;
    setCurrentDependerType(type);
    setSelectedTickers(tickers);
    setCurrentFieldsCount(entity?.fields?.length || 0);
  }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFields = useCallback(
    ({ fields: f, availableExtras }) => {
      if (!f || !f.form) return undefined;
      return f.form.map(it => {
        if (it.fieldName === "dependersBoxes") {
          return {
            ...it,
            options: availableExtras,
            visible: ({ formValues }) => {
              const type = formValues.dependers || DependerType.EXTRA;
              const fieldsCount = formValues.fields?.length || 0;
              if (
                type !== currentDependerTypeRef.current ||
                fieldsCount !== currentFieldsCountRef.current
              ) {
                queueMicrotask(() => {
                  if (fieldsCount !== currentFieldsCountRef.current) {
                    currentFieldsCountRef.current = fieldsCount;
                    setCurrentFieldsCount(fieldsCount);
                  }
                  if (type !== currentDependerTypeRef.current) {
                    currentDependerTypeRef.current = type;
                    setCurrentDependerType(type);
                    setSelectedTickers([]);
                  }
                });
              }
              return formValues.dependers === DependerType.EXTRA;
            },
          };
        }
        return it;
      });
    },
    [] // refs and state setters are stable; no deps needed
  );

  const entityFormFields = useMemo(
    () => getFields({ fields, availableExtras: otherProps.wbkData?.extras || [] }),
    [getFields, fields, otherProps.wbkData?.extras]
  );

  const handleInventoryChange = useCallback(it => {
    const newValue = it && typeof it === "object" && "value" in it ? it.value : it;
    setSelectedTickers(Array.isArray(newValue) ? newValue : []);
  }, []);

  const { onSubmitEntity } = otherProps;
  const handleSubmit = useCallback(
    ({ formValues }) => {
      const ent = prepareEntityForSubmit({ formValues, selectedTickers });
      onSubmitEntity({ entity: ent });
    },
    [selectedTickers, onSubmitEntity]
  );

  const showInventorySelector = INVENTORY_SELECTOR_TYPES.includes(currentDependerType);
  const isRoomStayMaxReached =
    currentDependerType === DependerType.ROOMSTAY_CHECKOUT && currentFieldsCount >= 1;

  const selectorKey = `${currentDependerType}_${entity?.id || "new"}`;

  const validationErrorCode = entityError?.message?.errorCode;
  let localizedValidationError = null;
  if (validationErrorCode) {
    const descriptor = QUESTIONNAIRE_VALIDATION_MESSAGES[validationErrorCode];
    localizedValidationError = descriptor
      ? intl.formatMessage(descriptor)
      : entityError.message.message || validationErrorCode;
  }

  let inventoryControl = null;
  if (showInventorySelector && loadChainInventoryLinesAllowed) {
    inventoryControl = (
      <SmartSelectorList
        key={selectorKey}
        formValues={entity || {}}
        initialValues={selectedTickers}
        value={selectedTickers}
        field={{
          disabled: false,
          fieldName: "dependersBoxes",
          filterNames,
          defaultValue: [],
          options: allowedChainInventoryLinesOptions,
          propertyTickers: allowedChainInventoryLinesTickers,
          staticFilters: {},
          onEditByExcel: undefined,
        }}
        onChange={({ value }) =>
          setSelectedTickers(Array.isArray(value) ? value.flat().filter(Boolean) : [])
        }
      />
    );
  } else if (showInventorySelector && loadInventoryLinesAllowed) {
    inventoryControl = (
      <SmartSelectorV2
        key={selectorKey}
        initialValues={selectedTickers}
        value={selectedTickers}
        field={{
          disabled: false,
          fieldName: "dependersBoxes",
          filterNames,
          defaultValue: [],
          options: allowedInventoryLines,
          staticFilters: {},
          selectedLabel: intl.formatMessage(filterMessages.selected),
        }}
        onChange={handleInventoryChange}
      />
    );
  }

  const inventorySelector =
    inventoryControl || localizedValidationError ? (
      <>
        {inventoryControl && (
          <InventorySection title={intl.formatMessage(filterMessages.inventoryLines)}>
            {inventoryControl}
          </InventorySection>
        )}
        {localizedValidationError && (
          <div style={{ marginBottom: 16 }}>
            <EntityErrorMessage entityError={{ localMessage: localizedValidationError }} />
          </div>
        )}
      </>
    ) : null;

  return (
    <QuestionnaireFormContext.Provider value={{ isRoomStayMaxReached, currentDependerType }}>
      <div className={"wbkv9-form-questionnaire"}>
        <EntityForm
          loading={loading}
          name={name}
          entity={entity}
          entities={entities}
          validation={validation}
          renderers={renderers}
          fields={entityFormFields}
          locales={locales}
          translation={translation}
          onSubmit={handleSubmit}
          entityError={validationErrorCode ? undefined : entityError}
          entityResult={entityResult}
          getListPath={getListPath}
          nestedFormWidgets={nestedFormWidgets}
          nestedListWidgets={nestedListWidgets}
          entityToFormValuesMapper={customEntityToFormValuesMapper}
          beforeSubmit={inventorySelector}
          {...otherProps}
        />
      </div>
    </QuestionnaireFormContext.Provider>
  );
}

export default withPropertyData(QuestionnairesForm);
